import React, { useState, useEffect } from 'react';
import ListBox from './ListBox';

export interface GenericSearchBarProps<T> {
  searchFunction: (query: string, signal: AbortSignal) => Promise<T[] | null | undefined>;
  renderLabel: (item: T) => React.ReactNode;
  placeholder?: string;
  minQueryLength?: number;
  onSelect?: (item: T) => void;
}

export function GenericSearchBar<T>({
  searchFunction,
  renderLabel,
  placeholder = 'Search...',
  minQueryLength = 2,
  onSelect,
}: GenericSearchBarProps<T>): React.ReactElement {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<T[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  useEffect(() => {
    if (query.length < minQueryLength) {
      setResults([]);
      setActiveIndex(-1);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const data = await searchFunction(query, controller.signal);
        setResults(data || []);
        setActiveIndex(0); // auto-select first
      } catch (error: unknown) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [query, searchFunction, minQueryLength]);

  const handleSelect = (index: number) => {
    const selectedItem = results[index];
    if (selectedItem && onSelect) {
      onSelect(selectedItem);
    }
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    }
    if (e.key === 'ArrowUp') {
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(activeIndex);
    }
  };

  return (
    <div>
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {results.length > 0 && (
        <ListBox
          items={results}
          activeIndex={activeIndex}
          renderLabel={renderLabel}
          onClickItem={handleSelect}
        />
      )}
    </div>
  );
}

export default GenericSearchBar;